const ATTRIBUTE_DELIMITER = /[<>"'`]/;
const ENTITY = /&(?:#x([0-9a-f]+)|#([0-9]+)|colon|quot|apos);/gi;

const hasControlOrSpace = (value: string): boolean => Array.from(value).some((character) => {
  const codePoint = character.codePointAt(0);
  return codePoint !== undefined && (codePoint <= 0x20 || (codePoint >= 0x7f && codePoint <= 0x9f));
});

const decodeEntities = (value: string): string => {
  let decoded = value;
  for (let index = 0; index < 4; index += 1) {
    const next = decoded
      .replace(/&amp;/gi, '&')
      .replace(ENTITY, (_match, hex, decimal) => {
        if (_match.toLowerCase() === '&colon;') return ':';
        if (_match.toLowerCase() === '&quot;') return '"';
        if (_match.toLowerCase() === '&apos;') return "'";
        const codePoint = Number.parseInt(hex || decimal, hex ? 16 : 10);
        return Number.isSafeInteger(codePoint)
          && codePoint <= 0x10ffff
          && (codePoint < 0xd800 || codePoint > 0xdfff)
          ? String.fromCodePoint(codePoint)
          : '';
      });
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
};

const normalizedUrl = (value: string): string | null => {
  const decoded = decodeEntities(value);
  return hasControlOrSpace(decoded) || ATTRIBUTE_DELIMITER.test(decoded) ? null : decoded;
};

const isSafeRelativeUrl = (value: string): boolean =>
  value.startsWith('#') || value.startsWith('./') || value.startsWith('../') || (value.startsWith('/') && !value.startsWith('//'));

/** URL policy for user-authored markdown. Never use this for trusted app routes. */
export const safeMarkdownLinkUrl = (value: string): string | null => {
  const url = normalizedUrl(value);
  return url && (/^(https?:|mailto:)/i.test(url) || isSafeRelativeUrl(url)) ? url : null;
};

/** Media allows attachment object URLs and narrowly-scoped inline image data only. */
export const safeMarkdownMediaUrl = (value: string): string | null => {
  const url = normalizedUrl(value);
  return url && (/^https?:/i.test(url)
    || isSafeRelativeUrl(url)
    || /^blob:/i.test(url)
    || /^data:image\/(?:png|gif|jpe?g|webp);base64,[a-z0-9+/=]+$/i.test(url))
    ? url
    : null;
};

export const isSafeMarkdownLinkUrl = (value: string): boolean => safeMarkdownLinkUrl(value) !== null;
export const isSafeMarkdownMediaUrl = (value: string): boolean => safeMarkdownMediaUrl(value) !== null;

/** Escapes every HTML attribute delimiter; text escaping alone does not quote-escape. */
export const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
